import { globalStores } from '@/editor/stores';
import FaceEntity from '@/editor/entities/faceEntity';
import { EEntityType } from '@/editor/types';

export class FaceManager {
  private _faceList: FaceEntity[] = [];

  constructor() {}

  get entities() {
    return this._faceList;
  }

  private getDataLists() {
    const wallHiddenIds = globalStores.visibilityStore.getHiddenEntitiesIdsByEntityType(EEntityType.Wall);
    const sepWallHiddenIds = globalStores.visibilityStore.getHiddenEntitiesIdsByEntityType(EEntityType.SeparateWall);
    const columnHiddenIds = globalStores.visibilityStore.getHiddenEntitiesIdsByEntityType(EEntityType.Column);

    const scene3dViewChangeHiddenWallIds = globalStores.visibilityStore.get3dViewChangeHiddenWallIds();
    const finalWallHiddenIds = new Set([...wallHiddenIds, ...scene3dViewChangeHiddenWallIds]);
    const isHidden = (hiddenIds: Set<string>, id: string | undefined) => (id !== undefined ? hiddenIds.has(id) : false);

    return {
      wallFaceDatas: globalStores.faceStore
        .getWallFaceDatas()
        .filter((faceData) => !isHidden(finalWallHiddenIds, faceData.relativeInfo.wallId)),
      sepWallFaceDatas: globalStores.faceStore
        .getSepWallFaceDatas()
        .filter(
          (faceData) =>
            !(
              isHidden(sepWallHiddenIds, faceData.relativeInfo.sepId) ||
              isHidden(finalWallHiddenIds, faceData.relativeInfo.wallId)
            )
        ),
      columnFaceDatas: globalStores.faceStore
        .getColumnFaceDatas()
        .filter((faceData) => !isHidden(columnHiddenIds, faceData.relativeInfo.columnId)),
      holeFaceDatas: globalStores.faceStore.getHoleFaceDatas().filter((faceData) => {
        return !(
          isHidden(finalWallHiddenIds, faceData.relativeInfo.wallId) ||
          isHidden(sepWallHiddenIds, faceData.relativeInfo.sepId)
        );
      })
    };
  }

  private updateFace(forceUpdate: boolean = false) {
    const { wallFaceDatas, sepWallFaceDatas, columnFaceDatas, holeFaceDatas } = this.getDataLists();
    const newFaceDataMap = new Map(
      [...wallFaceDatas, ...sepWallFaceDatas, ...columnFaceDatas, ...holeFaceDatas].map((faceData) => [
        faceData.id,
        faceData
      ])
    );

    const _cacheHandled = new Set<string>();

    // 删除/更新
    const len = this._faceList.length;
    for (let i = len - 1; i >= 0; i--) {
      const entity = this._faceList[i];

      const entityId = entity.id;
      _cacheHandled.add(entityId);
      const newFaceData = newFaceDataMap.get(entityId);

      if (!newFaceData) {
        this.deleteEntity(i);
        continue;
      }

      entity.update({ ...newFaceData, forceUpdate });

      newFaceDataMap.delete(entityId);
    }

    // 新增
    for (const faceData of newFaceDataMap.values()) {
      if (_cacheHandled.has(faceData.id)) continue;

      this.addEntity(new FaceEntity(faceData));
    }
  }

  update() {
    this.updateFace();
  }

  forceUpdate() {
    this.updateFace(true);
  }

  getEntity(entityId: string) {
    return this._faceList.find((entity) => entity.id === entityId);
  }

  /**
   * 只收 id，面数据由本方法自己从 store 取。
   *
   * store 用 immer 生成新对象，调用方在写 store 之前取到的那份是死快照；
   * 若由调用方传入，改完材质传回来的仍是旧 materialInfo，FaceEntity.update 的 diff 判等、
   * 模型不更新，要等 3d 视角变化触发全量 update 才生效。签名对齐 RegionManager.updateEntity
   */
  updateEntity(faceId: string) {
    const faceData = globalStores.faceStore.getFaceData(faceId);
    if (!faceData) return;

    const entity = this.getEntity(faceId);
    if (!entity) return;

    entity.update(faceData);
  }

  deleteEntity(index: number) {
    if (index >= this._faceList.length || index < 0) return;
    this._faceList[index].dispose();
    this._faceList.splice(index, 1);
  }

  addEntity(entity: FaceEntity) {
    this._faceList.push(entity);
  }
}
